// Tipos del contrato HITL adaptados de langchain-ai/agent-inbox (MIT).
//
// Define el contrato estandar HumanInterrupt / HumanResponse usado para
// resolver aprobaciones Human-in-the-Loop en la pantalla "Agent Inbox".
// Helpers (prettifyText, createDefaultHumanResponses) adaptados de
// src/components/agent-inbox/utils.ts del repo original.

/** Flags que indican que acciones puede tomar el humano sobre un interrupt. */
export interface HumanInterruptConfig {
  allow_ignore: boolean;
  allow_respond: boolean;
  allow_edit: boolean;
  allow_accept: boolean;
}

/** Accion que el humano revisa/edita. */
export interface ActionRequest {
  /** Nombre legible de la accion. */
  action: string;
  /** Args que el humano revisa/edita. */
  args: Record<string, unknown>;
}

/** Interrupt emitido por el agente cuando necesita aprobacion humana. */
export interface HumanInterrupt {
  action_request: ActionRequest;
  config: HumanInterruptConfig;
  /** Descripcion en markdown. */
  description?: string;
}

/**
 * Respuesta del humano a un interrupt.
 * Semantica de `args` por tipo:
 *  - accept   -> el ActionRequest original (o null)
 *  - edit     -> ActionRequest con args modificados
 *  - response -> string (texto libre)
 *  - ignore   -> null
 */
export type HumanResponse = {
  type: "accept" | "ignore" | "response" | "edit";
  args: null | string | ActionRequest;
};

/** Tipos de envio que el formulario de la inbox puede disparar. */
export type SubmitType = "accept" | "response" | "edit";

/**
 * Convierte un identificador (snake_case / kebab-case) en Start Case legible.
 * Ej: "aprobar_imputacion" -> "Aprobar Imputacion".
 */
export function prettifyText(text: string): string {
  return text
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * A partir de los flags `config.allow_*` de un interrupt, construye la lista
 * de HumanResponse posibles y elige el submit por defecto con prioridad
 * accept > response > edit.
 */
export function createDefaultHumanResponses(interrupt: HumanInterrupt): {
  responses: HumanResponse[];
  defaultSubmitType: SubmitType | undefined;
  hasAccept: boolean;
} {
  const { config, action_request } = interrupt;
  const responses: HumanResponse[] = [];

  if (config.allow_edit) {
    // Copia del action_request para prerellenar el formulario de edicion.
    responses.push({
      type: "edit",
      args: {
        action: action_request.action,
        args: { ...action_request.args },
      },
    });
  }
  if (config.allow_respond) {
    responses.push({ type: "response", args: "" });
  }
  if (config.allow_accept) {
    responses.push({ type: "accept", args: action_request });
  }
  if (config.allow_ignore) {
    responses.push({ type: "ignore", args: null });
  }

  let defaultSubmitType: SubmitType | undefined;
  if (config.allow_accept) {
    defaultSubmitType = "accept";
  } else if (config.allow_respond) {
    defaultSubmitType = "response";
  } else if (config.allow_edit) {
    defaultSubmitType = "edit";
  }

  return {
    responses,
    defaultSubmitType,
    hasAccept: config.allow_accept,
  };
}
